import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Trash2, Sparkles } from "lucide-react";

/**
 * AI Help Chat — floating bubble that streams Claude Sonnet 4.6 answers
 * from POST /api/chat/message (SSE). Session-only history (localStorage
 * session_id, cleared server-side on trash click).
 */

const SESSION_KEY = "gp_chat_session";
const MESSAGES_KEY = "gp_chat_history";

// Generate a stable session id; keep it across reloads so multi-turn history
// on the backend still resolves. Trash icon wipes both this + backend cache.
const getOrCreateSessionId = () => {
  let id = null;
  try { id = localStorage.getItem(SESSION_KEY); } catch { /* no-op */ }
  if (!id) {
    id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem(SESSION_KEY, id); } catch { /* no-op */ }
  }
  return id;
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const saveHistory = (msgs) => {
  try { localStorage.setItem(MESSAGES_KEY, JSON.stringify(msgs.slice(-40))); } catch { /* no-op */ }
};

const WELCOME = {
  role: "assistant",
  content: "Hi! I'm Coco 👋 Ask me anything about GalerieCop — pricing, features, uploads, RSVPs, or how QR codes work.",
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => {
    const stored = loadHistory();
    return stored.length ? stored : [WELCOME];
  });
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getOrCreateSessionId()).current;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    saveHistory(messages);
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: "user", content: text };
    // Reserve an empty assistant slot we'll stream into.
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    try {
      const resp = await fetch(`${base}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // Parse complete SSE frames (\n\n terminated).
        const frames = buf.split("\n\n");
        buf = frames.pop() ?? "";
        for (const frame of frames) {
          const lines = frame.split("\n");
          let evt = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) evt = line.slice(7).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          if (evt === "token") {
            try {
              const chunk = JSON.parse(data);
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: (copy[copy.length - 1]?.content || "") + chunk,
                };
                return copy;
              });
            } catch { /* ignore malformed */ }
          } else if (evt === "error") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: "Sorry, something went wrong on my side. Please email support@galeriecop.com and we'll help you.",
              };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "I can't reach the server right now. Please email support@galeriecop.com and we'll help you.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    try { await fetch(`${base}/api/chat/session/${sessionId}`, { method: "DELETE" }); } catch { /* no-op */ }
    setMessages([WELCOME]);
    try { localStorage.removeItem(MESSAGES_KEY); } catch { /* no-op */ }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="chat-widget-root">
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="mb-3 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-cream-200 overflow-hidden flex flex-col"
          style={{ maxHeight: "min(560px, calc(100vh - 120px))" }}
        >
          <div className="bg-coral-300 px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-semibold text-sm" data-testid="chat-title">Ask Coco</div>
                <div className="text-[11px] opacity-90">AI help · powered by Claude</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={clearChat}
                title="Clear chat"
                data-testid="chat-clear-btn"
                className="text-white/90 hover:text-white p-1 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close-btn"
                className="text-white/90 hover:text-white p-1 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 p-4 bg-cream-100/60 overflow-y-auto space-y-2"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`text-sm rounded-lg p-3 shadow-sm max-w-[85%] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-navy-700 text-white ml-auto"
                    : "bg-white text-navy-700"
                }`}
              >
                {m.content || (streaming && i === messages.length - 1 ? (
                  <span className="inline-flex gap-1 items-center opacity-70">
                    <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                ) : null)}
              </div>
            ))}
          </div>

          <form onSubmit={send} className="p-3 border-t border-cream-200 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming}
              placeholder={streaming ? "Coco is typing…" : "Ask about pricing, uploads, RSVPs…"}
              data-testid="chat-input"
              className="flex-1 text-sm px-3 py-2 rounded-md border border-cream-200 focus:outline-none focus:border-coral-300 disabled:bg-cream-100/60"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="bg-coral-300 text-white px-3 rounded-md hover:bg-coral-400 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        className="bg-coral-300 hover:bg-coral-400 text-white rounded-full shadow-lg pl-4 pr-5 py-3 flex items-center gap-2 transition-colors"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-medium">{open ? "Close" : "Ask Coco"}</span>
      </button>
    </div>
  );
};

export default ChatWidget;
